import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '../config.ts';
import { LogoutButton } from '../features/auth/LogoutButton.tsx';

/**
 * Estructura de la aplicación con sesión iniciada.
 *
 * Mobile first de verdad: la navegación es una barra INFERIOR en móvil, donde llega el
 * pulgar, y se sube a la cabecera a partir de `sm`. El contenido lleva relleno inferior
 * para no quedar tapado por la barra.
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-dvh flex-col">
      <a className="salto-contenido" href="#contenido">
        {t('nav.skipToContent')}
      </a>

      <header className="sticky top-0 z-10 border-b border-borde bg-superficie pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between gap-4 px-4 py-3">
          <NavLink to="/" className="text-base font-bold tracking-tight text-texto no-underline">
            {APP_NAME}
          </NavLink>
          <nav className="hidden sm:block" aria-label={t('nav.menu')}>
            <Enlaces />
          </nav>
          <LogoutButton />
        </div>
      </header>

      <main id="contenido" className="mx-auto w-full max-w-4xl flex-1 px-4 py-4 pb-28 sm:pb-4">
        {children}
      </main>

      {/* Barra inferior, solo en móvil. */}
      <nav
        className="fixed inset-x-0 bottom-0 z-10 border-t border-borde bg-superficie pb-[env(safe-area-inset-bottom)] sm:hidden"
        aria-label={t('nav.menu')}
      >
        <div className="flex">
          <Enlaces variante="barra" />
        </div>
      </nav>

      <footer className="mx-auto w-full max-w-4xl px-4 pb-4 text-xs text-tenue max-sm:hidden">
        {t('app.noMoney')}
      </footer>
    </div>
  );
}

function Enlaces({ variante = 'cabecera' }: { variante?: 'cabecera' | 'barra' }) {
  const { t } = useTranslation();
  const enBarra = variante === 'barra';

  const rutas = [
    { to: '/', etiqueta: t('nav.leagues'), exacta: true },
    { to: '/leagues/join', etiqueta: t('nav.join'), exacta: false },
  ];

  return (
    <ul className={enBarra ? 'flex w-full list-none' : 'flex list-none gap-4'}>
      {rutas.map((ruta) => (
        <li key={ruta.to} className={enBarra ? 'flex-1' : ''}>
          <NavLink
            to={ruta.to}
            end={ruta.exacta}
            className={({ isActive }) =>
              [
                enBarra
                  ? 'flex min-h-toque items-center justify-center px-2 py-3 text-sm'
                  : 'text-sm',
                'no-underline',
                isActive ? 'font-bold text-acento' : 'text-tenue',
              ].join(' ')
            }
          >
            {ruta.etiqueta}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
